// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { Filter } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIVITY_FILTER_DEFAULTS,
  type ActivityEntry,
  type ActivityFilterState,
  ActivityTimeline,
} from "@/components/shared/activity-timeline";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";

function generateMockData(): ActivityEntry[] {
  const now = Date.now();
  const day = 86_400_000;
  return [
    {
      id: 1,
      timestamp: new Date(now - 0.2 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Chain Settings",
      field: "defaultTimeZone",
      oldValue: "America/Chicago",
      newValue: "America/New_York",
      tag: "chain",
    },
    {
      id: 2,
      timestamp: new Date(now - 0.6 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Setting Override",
      field: "enable_promo_scoring",
      oldValue: "false",
      newValue: "true",
      tag: "chain",
      message: "Enabling promo scoring chain-wide for Q2 campaign rollout.",
    },
    {
      id: 3,
      timestamp: new Date(now - 1.5 * day).toISOString(),
      user: "ops@focal.systems",
      action: "created",
      target: "Region",
      field: "West Coast",
      tag: "chain",
    },
    {
      id: 4,
      timestamp: new Date(now - 2 * day).toISOString(),
      user: "admin@focal.systems",
      action: "updated",
      target: "Chain Info",
      field: "legibleName",
      oldValue: "Acme Groceries",
      newValue: "Acme Fresh Markets",
      tag: "chain",
    },
    {
      id: 5,
      timestamp: new Date(now - 4 * day).toISOString(),
      user: "ops@focal.systems",
      action: "created",
      target: "Store",
      field: "acme_store_47",
      tag: "store",
    },
    {
      id: 6,
      timestamp: new Date(now - 5 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Store Settings",
      field: "min_workable_items",
      oldValue: "3",
      newValue: "5",
      tag: "store",
    },
    {
      id: 7,
      timestamp: new Date(now - 8 * day).toISOString(),
      user: "admin@focal.systems",
      action: "deleted",
      target: "Region",
      field: "Southeast",
      tag: "chain",
    },
    {
      id: 8,
      timestamp: new Date(now - 10 * day).toISOString(),
      user: "ops@focal.systems",
      action: "restored",
      target: "Setting Override",
      field: "sorting_logic",
      tag: "chain",
    },
    {
      id: 9,
      timestamp: new Date(now - 14 * day).toISOString(),
      user: "admin@focal.systems",
      action: "activated",
      target: "Chain",
      tag: "chain",
    },
    {
      id: 10,
      timestamp: new Date(now - 18 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Import Config",
      field: "importSchedule",
      oldValue: "daily",
      newValue: "hourly",
      tag: "chain",
      message: "Switching to hourly imports ahead of inventory reset season.",
    },
    {
      id: 11,
      timestamp: new Date(now - 20 * day).toISOString(),
      user: "ops@focal.systems",
      action: "activated",
      target: "Store",
      field: "acme_store_45",
      tag: "store",
    },
    {
      id: 12,
      timestamp: new Date(now - 22 * day).toISOString(),
      user: "ops@focal.systems",
      action: "created",
      target: "Store",
      field: "acme_store_46",
      tag: "store",
    },
    {
      id: 13,
      timestamp: new Date(now - 30 * day).toISOString(),
      user: "admin@focal.systems",
      action: "deactivated",
      target: "Chain",
      tag: "chain",
      message: "Chain temporarily deactivated for data migration.",
    },
    {
      id: 14,
      timestamp: new Date(now - 40 * day).toISOString(),
      user: "ops@focal.systems",
      action: "updated",
      target: "Setting Override",
      field: "priority_logic",
      oldValue: "promo",
      newValue: "lost_sales",
      tag: "chain",
    },
    {
      id: 15,
      timestamp: new Date(now - 55 * day).toISOString(),
      user: "admin@focal.systems",
      action: "created",
      target: "Region",
      field: "Midwest",
      tag: "chain",
    },
    {
      id: 16,
      timestamp: new Date(now - 70 * day).toISOString(),
      user: "lukasz@focal.systems",
      action: "updated",
      target: "Chain Info",
      field: "storeCode",
      oldValue: "ACME",
      newValue: "ACMF",
      tag: "chain",
    },
    {
      id: 17,
      timestamp: new Date(now - 80 * day).toISOString(),
      user: "admin@focal.systems",
      action: "created",
      target: "Chain",
      tag: "chain",
    },
  ];
}

const ALL_DATA = generateMockData();

interface ChainActivityTabProps {
  chainName: string;
  initialFilters?: string;
}

const CHAIN_FILTER_DEFAULTS = {
  ...ACTIVITY_FILTER_DEFAULTS,
  onlyChain: true,
};

export function ChainActivityTab({ chainName, initialFilters }: ChainActivityTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const initial = useMemo(() => decodeFilters(initialFilters, CHAIN_FILTER_DEFAULTS), [initialFilters]);
  const [onlyChain, setOnlyChain] = useState(initial.onlyChain);

  const entries = onlyChain ? ALL_DATA.filter((e) => e.tag === "chain") : ALL_DATA;

  const syncUrl = useCallback(
    (values: ActivityFilterState & { onlyChain: boolean }) => {
      const encoded = encodeFilters(values, CHAIN_FILTER_DEFAULTS);
      navigate({
        to: "/chains/$chainName",
        params: { chainName },
        search: { t: "activity", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, chainName],
  );

  const handleFilterChange = useCallback(
    (base: ActivityFilterState) => {
      syncUrl({ ...base, onlyChain });
    },
    [syncUrl, onlyChain],
  );

  const handleOnlyChainToggle = useCallback(() => {
    setOnlyChain((v) => {
      const next = !v;
      // Sync immediately with the toggled value
      syncUrl({ search: "", timeRange: "30", onlyChain: next });
      return next;
    });
  }, [syncUrl]);

  return (
    <ActivityTimeline
      entries={entries}
      filters={
        <FilterToggle
          active={onlyChain}
          onChange={handleOnlyChainToggle}
          icon={Filter}
          label={t("activity.filter.onlyChain")}
        />
      }
      hasActiveFilters={!onlyChain}
      onReset={() => setOnlyChain(CHAIN_FILTER_DEFAULTS.onlyChain)}
      initialFilters={initial}
      onFilterChange={handleFilterChange}
    />
  );
}
